import * as React from 'react';
import { DialerPhoneCallStatus, useTelephony } from '../../../contexts/telephony';
import { useQueue } from '../../../hooks/queue';
import { useRealCallCoaching } from '../../hooks/useRealCallCoaching';
import { useRealCallCoachingSession } from '../../hooks/useRealCallCoachingSession';

/**
 * Bridge component that connects real Twilio calls to the coaching system.
 * Renders nothing - purely handles side effects.
 */
export const RealCallCoachingBridge: React.FC = () => {
	const { phoneCall, phoneCallStatus } = useTelephony();
	const queue = useQueue();
	const [hasRequestedSummary, setHasRequestedSummary] = React.useState(false);

	const prevStatusRef = React.useRef<DialerPhoneCallStatus>(phoneCallStatus);

	const { requestEndOfCallSummary, isReady } = useRealCallCoachingSession({
		companyId: queue?.companyId || phoneCall?.companyId || null,
		contactId: phoneCall?.contactIds?.[0] || null,
		leadServedId: queue?.leadServedId,
		phoneCallId: phoneCall?.id,
	});

	// Bridge transcription events to coaching when call is connected
	const isCallActive = phoneCallStatus === DialerPhoneCallStatus.Connected;

	useRealCallCoaching({
		phoneCallId: phoneCall?.id || null,
		enabled: isCallActive && isReady,
	});

	// Request end-of-call summary when call ends
	React.useEffect(() => {
		// Detect transition: was Connected, now NoCall
		const wasConnected = prevStatusRef.current === DialerPhoneCallStatus.Connected;
		const isNowEnded = phoneCallStatus === DialerPhoneCallStatus.NoCall;

		if (wasConnected && isNowEnded && isReady && !hasRequestedSummary) {
			requestEndOfCallSummary();
			setHasRequestedSummary(true);
		}

		prevStatusRef.current = phoneCallStatus;
	}, [phoneCallStatus, isReady, hasRequestedSummary, requestEndOfCallSummary]);

	// Reset summary flag when new call starts
	React.useEffect(() => {
		if (phoneCallStatus === DialerPhoneCallStatus.Dialing) {
			setHasRequestedSummary(false);
		}
	}, [phoneCallStatus]);

	return null;
};
